import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Images, Loader2, Star, Trash2, ArrowLeft, ArrowRight, RefreshCw, ImagePlus, ImageOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;

const btnCls = "flex items-center gap-1 rounded-md border border-xmove-ink/15 bg-white px-2 py-1 text-[11px] font-bold text-xmove-ink transition-colors hover:border-xmove-orange hover:text-xmove-orange disabled:opacity-40";

export const FotoGestione = ({ veicolo, onClose, onChanged }) => {
  const mid = veicolo.id_motorizzazione;
  const [foto, setFoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const addRef = useRef(null);
  const replRef = useRef(null);
  const [replIdx, setReplIdx] = useState(null);

  const load = () => axios.get(`${API}/admin/veicoli/${mid}/foto`).then((r) => setFoto(r.data)).catch(() => setFoto([]));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (fn, okMsg) => {
    setBusy(true);
    try {
      await fn();
      okMsg && toast.success(okMsg);
      await load();
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Operazione non riuscita, riprova.");
    } finally {
      setBusy(false);
    }
  };

  const riordina = (ordine, msg) => run(() => axios.put(`${API}/admin/veicoli/${mid}/foto/ordine`, { ordine }), msg);
  const idxs = (foto || []).map((f) => f.idx);
  const sposta = (i, delta) => {
    const o = [...idxs];
    const j = i + delta;
    if (j < 0 || j >= o.length) return;
    [o[i], o[j]] = [o[j], o[i]];
    riordina(o);
  };
  const copertina = (i) => riordina([idxs[i], ...idxs.filter((_, k) => k !== i)], "Copertina aggiornata.");
  const elimina = (f) => {
    if (!window.confirm("Eliminare definitivamente questa foto?")) return;
    run(() => axios.delete(`${API}/admin/veicoli/${mid}/foto/${f.idx}`), "Foto eliminata.");
  };

  const aggiungi = async (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    if (!files.length) return;
    await run(async () => {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${API}/admin/veicoli/${mid}/foto`, fd);
      }
    }, `${files.length} foto aggiunt${files.length === 1 ? "a" : "e"}.`);
  };

  const sostituisci = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || replIdx == null) return;
    const fd = new FormData();
    fd.append("file", file);
    await run(() => axios.post(`${API}/admin/veicoli/${mid}/foto/${replIdx}/sostituisci`, fd), "Foto sostituita.");
    setReplIdx(null);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto bg-white" data-testid="foto-gestione-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl text-xmove-ink">
            <Images size={18} className="text-xmove-orange" /> Foto — {veicolo.marca} {veicolo.modello} {veicolo.allestimento}
          </DialogTitle>
          <DialogDescription className="text-xs text-xmove-muted">
            La prima foto è la copertina usata nelle card del catalogo. Puoi riordinare, sostituire, eliminare o aggiungere immagini (JPG/PNG, max 10MB).
          </DialogDescription>
        </DialogHeader>

        <input ref={addRef} type="file" accept="image/*" multiple onChange={aggiungi} className="hidden" data-testid="foto-add-input" />
        <input ref={replRef} type="file" accept="image/*" onChange={sostituisci} className="hidden" data-testid="foto-replace-input" />

        {foto === null ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-xmove-orange" size={24} /></div>
        ) : foto.length === 0 ? (
          <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed border-xmove-ink/20 py-12 text-sm text-xmove-muted" data-testid="foto-empty">
            <ImageOff size={26} />
            {veicolo.n_foto
              ? "Le immagini attuali sono esempi esterni: aggiungi una foto per sostituirle e poterle gestire da qui."
              : "Nessuna foto caricata per questo veicolo."}
          </div>
        ) : (
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="foto-grid">
            {foto.map((f, i) => (
              <li key={f.url} className={`overflow-hidden rounded-xl border ${i === 0 ? "border-xmove-orange ring-2 ring-xmove-orange/30" : "border-xmove-ink/10"}`} data-testid={`foto-item-${i}`}>
                <div className="relative aspect-[4/3] bg-xmove-cream">
                  <img src={`${BASE}${f.url}`} alt={`Foto ${i + 1}`} className="h-full w-full object-cover" loading="lazy" />
                  {i === 0 && (
                    <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-xmove-orange px-2 py-0.5 text-[10px] font-bold uppercase text-white" data-testid="foto-cover-badge">
                      <Star size={10} /> Copertina
                    </span>
                  )}
                  <span className="absolute right-2 top-2 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-bold text-white">{i + 1}/{foto.length}</span>
                </div>
                <div className="flex flex-wrap gap-1 p-2">
                  <button type="button" disabled={busy || i === 0} onClick={() => sposta(i, -1)} title="Sposta a sinistra" className={btnCls} data-testid={`foto-left-${i}`}><ArrowLeft size={11} /></button>
                  <button type="button" disabled={busy || i === foto.length - 1} onClick={() => sposta(i, 1)} title="Sposta a destra" className={btnCls} data-testid={`foto-right-${i}`}><ArrowRight size={11} /></button>
                  {i !== 0 && (
                    <button type="button" disabled={busy} onClick={() => copertina(i)} className={btnCls} data-testid={`foto-cover-${i}`}><Star size={11} /> Copertina</button>
                  )}
                  <button type="button" disabled={busy} onClick={() => { setReplIdx(f.idx); replRef.current?.click(); }} className={btnCls} data-testid={`foto-replace-${i}`}><RefreshCw size={11} /> Sostituisci</button>
                  <button type="button" disabled={busy} onClick={() => elimina(f)} className={`${btnCls} border-red-200 text-red-500 hover:border-red-500 hover:bg-red-500 hover:text-white`} data-testid={`foto-delete-${i}`}><Trash2 size={11} /></button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-xmove-ink/8 pt-4">
          <span className="text-xs text-xmove-muted">{busy ? "Salvataggio in corso…" : `${foto?.length ?? 0} foto`}</span>
          <div className="flex gap-2">
            <button type="button" onClick={() => addRef.current?.click()} disabled={busy} data-testid="foto-add-btn" className="flex items-center gap-1.5 rounded-lg bg-xmove-orange px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-xmove-orangeDark disabled:opacity-60">
              {busy ? <Loader2 size={13} className="animate-spin" /> : <ImagePlus size={13} />} Aggiungi foto
            </button>
            <button type="button" onClick={onClose} data-testid="foto-close-btn" className="rounded-lg border border-xmove-ink/15 px-4 py-2 text-xs font-bold text-xmove-muted transition-colors hover:text-xmove-ink">
              Chiudi
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
